'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { assertNever } from '@/lib/typescript/assertNever'
import { Card } from '@/ui/cards/card/Card'
import { Divider } from '@/ui/layouts/divider/Divider'
import { Skin } from '@/ui/skin/Skin'
import { Title } from '@/ui/text/title/Title'
import { InfoTooltip } from '@/ui/tooltips/info-tooltip/InfoTooltip.client'
import type { ShippingFeeTag_FeeFragment } from '@gql-generated/graphql'
import type { PropsWithChildren } from 'react'

export interface SummaryCardData {
  subtotalExclTax?: number | null
  totalExclTax?: number | null
  totalInclTax?: number | null
  totalDiscount?: number | null
  totalEcoPart?: number | null
  amountUntilFreeDelivery?: number | null
  paymentCode?: string | null
  fee?: ShippingFeeTag_FeeFragment | null
  taxesDetails?: ReadonlyArray<{
    amount?: { value?: number | null } | null
    label?: string | null
  } | null> | null
}

export interface SummaryCardProps extends PropsWithChildren {
  className?: string
  data: SummaryCardData
  divider?: boolean
  showTitle?: boolean
  compact?: boolean
  hideDiscount?: boolean
}

export function SalesSummaryCard({
  className,
  data,
  compact = false,
  divider = false,
  showTitle = false,
  hideDiscount = false,
  children,
}: SummaryCardProps) {
  const { t, Trans } = useTranslation(['salesSummaryCard'])

  const {
    subtotalExclTax,
    totalExclTax,
    totalInclTax,
    totalDiscount,
    totalEcoPart,
    amountUntilFreeDelivery,
    paymentCode,
    fee,
    taxesDetails,
  } = data

  const shippingFeeTooltipText = t('salesSummaryCard:shippingFeeTooltip')

  return (
    <Card
      className={twMerge(className, 'flex w-full flex-col gap-2')}
      radius="sm"
      padding="lg"
      skin="body"
    >
      {showTitle && (
        <Title size="4xl" className="font-bold">
          {t('salesSummaryCard:title')}
        </Title>
      )}

      <div className={twMerge('flex flex-col gap-2', compact && 'gap-0.5')}>
        {paymentCode && (
          <span className="flex justify-between text-lg">
            <span>{t('salesSummaryCard:paymentMethod')}</span>
            <span className="font-bold">{t(`salesSummaryCard:paymentCode.${paymentCode}`)}</span>
          </span>
        )}

        {!!subtotalExclTax && (
          <span className="flex justify-between text-lg font-bold" data-testid="subtotal-excl-tax">
            <span>
              <Trans
                i18nKey="salesSummaryCard:subtotal"
                components={{ discount: <span className="text-md" /> }}
              />
            </span>
            <span>{formatPrice(subtotalExclTax)}</span>
          </span>
        )}

        {!!totalEcoPart && (
          <span className="flex justify-between text-lg">
            {t('salesSummaryCard:ecoFee')}
            <span>{formatPrice(totalEcoPart)}</span>
          </span>
        )}

        {fee && (
          <span className="text-lg">
            {(() => {
              const { __typename } = fee

              switch (__typename) {
                case 'ShippingFeeWithAmount':
                  return (
                    <div className="flex flex-col gap-1">
                      {!!fee.amount && (
                        <div className="flex justify-between">
                          <span className="flex items-center">
                            {t('salesSummaryCard:shippingFee')}
                          </span>
                          <div className="flex items-center">
                            {formatPrice(fee.amount)}
                            <span className="ml-1">
                              <InfoTooltip
                                placement="bottom end"
                                tooltipText={<span>{shippingFeeTooltipText}</span>}
                              />
                            </span>
                          </div>
                        </div>
                      )}
                      {!!amountUntilFreeDelivery && (
                        <Skin
                          className="flex justify-center rounded-sm p-1 text-center text-sm font-medium leading-lighter"
                          tint="brand-1-light"
                        >
                          {t('salesSummaryCard:amountLeftForFreeShipping', {
                            amount: formatPrice(amountUntilFreeDelivery),
                          })}
                        </Skin>
                      )}
                    </div>
                  )
                case 'ShippingFeeFree':
                  return (
                    <div className="flex justify-between">
                      <span className="flex items-center">{t('salesSummaryCard:shippingFee')}</span>
                      {fee.isFranco ? (
                        <div className="flex items-center font-bold">
                          {t('salesSummaryCard:offered')}
                          <span className="ml-1">
                            <InfoTooltip
                              placement="bottom end"
                              tooltipText={<span>{shippingFeeTooltipText}</span>}
                            />
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center font-bold">
                          {t('salesSummaryCard:free')}
                          <span className="ml-1">
                            <InfoTooltip
                              placement="bottom end"
                              tooltipText={<span>{shippingFeeTooltipText}</span>}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                  )
                case undefined:
                  return null
                default:
                  assertNever(__typename)
              }
            })()}
          </span>
        )}

        {divider && <Divider direction="horizontal" variant="line" />}

        <div className="flex flex-col gap-1">
          {(!!totalExclTax || fee === null) && (
            <span className="flex flex-col">
              {!!totalExclTax && (
                <span className="flex justify-between text-2xl font-bold text-brand-1-800">
                  {t('salesSummaryCard:totalExcludingTax')}
                  <span>{formatPrice(totalExclTax)}</span>
                </span>
              )}

              {fee === null && (
                <span className="text-xs">{t('salesSummaryCard:totalExclTaxMention')}</span>
              )}
            </span>
          )}

          {taxesDetails?.map((tax) => {
            const value = tax?.amount?.value

            if (!tax?.label || typeof value !== 'number') return null

            return (
              <span key={tax.label} className="flex justify-between text-lg">
                <span>{tax.label}</span>
                <span>{formatPrice(value)}</span>
              </span>
            )
          })}

          {!!totalInclTax && (
            <span className="flex justify-between text-lg">
              {t('salesSummaryCard:totalIncludingTax')}
              <span>{formatPrice(totalInclTax)}</span>
            </span>
          )}
        </div>

        {!!totalDiscount && !hideDiscount && (
          <span className="flex justify-between text-lg font-bold leading-light text-error-fg">
            {t('salesSummaryCard:discountAmount')}
            <span>{formatPrice(totalDiscount)}</span>
          </span>
        )}
      </div>

      {children}
    </Card>
  )
}
